import React from 'react';
import { SplitSquareHorizontal } from 'lucide-react';

import { formatCurrency } from '../../lib/utils';

/**
 * "This till report was split into two lines, and here is the proof."
 *
 * A mixed-rate Z-report posts as one gross line under U25, and the platform then charges 25% on
 * the VAT-exempt turnover too — 53,60 kr and 270,00 kr on the two real documents this was built
 * from. The backend now splits it automatically, but only when the report's own KATEGORISALG
 * breakdown reconciles with the derived exempt figure.
 *
 * This component exists because a SILENT split is the dangerous version. The accountant opens a
 * document expecting one line and finds two; if the screen does not say why, the options are to
 * trust it blindly or to redo the arithmetic by hand, and neither is review. So it shows the
 * derivation AND the independent evidence: the exempt figure derived from the VAT, the exempt
 * categories that sum to the same number, and the VAT that would otherwise have been overpaid.
 *
 * It deliberately does not render at all unless a split actually happened — an explanation for a
 * thing that did not occur is noise, and the mixed-rate guard speaks for the un-split case.
 */
export function hasSplit(split) {
    return Boolean(split && split.split);
}

export default function TillSplitNotice({ split, currency = 'DKK', className = '' }) {
    if (!hasSplit(split)) return null;

    const names = (split.exempt_categories || []).map((c) => c.name);
    const verified = split.verified || {};
    const money = (v) => formatCurrency(Number(v || 0), currency);

    return (
        <div
            data-testid="till-split-notice"
            className={`rounded border border-sky-300 bg-sky-50 p-3 text-sm text-sky-950 ${className}`}
        >
            <p className="flex items-center gap-2 font-medium">
                <SplitSquareHorizontal className="h-4 w-4 shrink-0" />
                Kasserapporten er delt i to linjer
            </p>

            <dl className="mt-2 grid gap-1 font-mono text-xs sm:grid-cols-[auto_1fr] sm:gap-x-3">
                <dt data-testid="till-split-rated-label">Momspligtigt ({split.sale_vat_code})</dt>
                <dd data-testid="till-split-rated">
                    {money(split.vat_rated_gross)} <span className="opacity-70">(moms {money(split.vat_on_rated)})</span>
                </dd>
                <dt data-testid="till-split-exempt-label">Momsfrit ({split.exempt_vat_code})</dt>
                <dd data-testid="till-split-exempt">{money(split.exempt_gross)}</dd>
                <dt className="border-t border-sky-200 pt-1">I alt</dt>
                <dd className="border-t border-sky-200 pt-1" data-testid="till-split-total">
                    {money(split.document_total)}
                </dd>
            </dl>

            {/* The evidence. Without this the split is an assertion; with it, it is checkable. */}
            <p className="mt-2 text-xs" data-testid="till-split-evidence">
                Kontrolleret mod kategorisalget: det momsfrie beløb beregnet ud fra momsen er{' '}
                <strong>{money(verified.derived_exempt)}</strong>, og{' '}
                {names.length > 0 ? <strong>{names.join(' + ')}</strong> : 'de momsfrie kategorier'}{' '}
                udgør <strong>{money(verified.exempt_from_categories)}</strong>
                {split.category_count ? ` (${split.category_count} kategorier i alt)` : ''}.
            </p>

            {Number(split.would_have_overstated_by) > 0 && (
                <p className="mt-1 text-xs" data-testid="till-split-saved">
                    Uden opdelingen ville systemet have beregnet{' '}
                    <strong>{money(split.would_have_overstated_by)}</strong> for meget i salgsmoms.
                </p>
            )}
        </div>
    );
}
